'use client';

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Room, RoomEvent, Track, type Participant, type TranscriptionSegment } from 'livekit-client';
import {
  RoomAudioRenderer,
  RoomContext,
  useChat,
  useLocalParticipant,
  useRemoteParticipants,
  useRoomContext,
} from '@livekit/components-react';
import styles from './AgentChatWidget.module.css';

type WidgetMode = 'admin' | 'candidate';
type Status = 'idle' | 'connecting' | 'connected' | 'collapsed';
const INTERVIEW_JOIN_START_EVENT = 'interview:join-start';
const CONNECT_TIMEOUT_MS = 30_000;
const ERROR_RESET_DELAY_MS = 2_000;
// Set to false to disable the "Listening..." animation while the user speaks.
const SHOW_LISTENING_ANIMATION = true;

type DisplayMessage =
  | { kind: 'chat'; id: string; text: string; isLocal: boolean; ts: number }
  | { kind: 'transcript'; id: string; text: string; isLocal: boolean; ts: number; isFinal: boolean; };

interface Props {
  mode: WidgetMode;
  participantName: string;
  startButtonLabel?: string;
}

export function AgentChatWidget({ mode, participantName, startButtonLabel = 'Start Call' }: Props) {
  const room = useMemo(() => new Room(), []);
  const [status, setStatus] = useState<Status>('idle');
  const [isHidden, setIsHidden] = useState(false);
  const [pos, setPos] = useState<{ bottom: number; right: number } | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const dragStateRef = useRef<{
    pointerX: number;
    pointerY: number;
    elemBottom: number;
    elemRight: number;
    started: boolean;
  } | null>(null);
  const suppressNextClickRef = useRef(false);
  const roomNameRef = useRef(`agent-${mode}-${Date.now()}`);
  const connectAttemptIdRef = useRef(0);
  const connectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const errorResetTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [error, setError] = useState<string | null>(null);

  const clampToViewport = useCallback((bottom: number, right: number) => {
    const el = containerRef.current;
    const width = el?.offsetWidth ?? 120;
    const height = el?.offsetHeight ?? 42;
    const pad = 12;
    return {
      right: Math.max(pad, Math.min(right, window.innerWidth - width - pad)),
      bottom: Math.max(pad, Math.min(bottom, window.innerHeight - height - pad)),
    };
  }, []);

  const scheduleErrorReset = useCallback(() => {
    if (!error) return;
    if (errorResetTimeoutRef.current !== null) {
      clearTimeout(errorResetTimeoutRef.current);
      errorResetTimeoutRef.current = null;
    }
    errorResetTimeoutRef.current = setTimeout(() => {
      setError(null);
      errorResetTimeoutRef.current = null;
    }, ERROR_RESET_DELAY_MS);
  }, [error]);

  const connect = useCallback(async () => {
    if (!participantName) return;
    connectAttemptIdRef.current += 1;
    const attemptId = connectAttemptIdRef.current;
    if (connectTimeoutRef.current !== null) {
      clearTimeout(connectTimeoutRef.current);
      connectTimeoutRef.current = null;
    }
    setStatus('connecting');
    setError(null);
    if (errorResetTimeoutRef.current !== null) {
      clearTimeout(errorResetTimeoutRef.current);
      errorResetTimeoutRef.current = null;
    }
    try {
      const timeoutPromise = new Promise<never>((_, reject) => {
        connectTimeoutRef.current = setTimeout(() => {
          reject(new Error('Connection timed out after 30 seconds. Please try again.'));
        }, CONNECT_TIMEOUT_MS);
      });

      const connectFlow = (async () => {
        const url = new URL('/api/widget-connection', window.location.origin);
        url.searchParams.set('roomName', roomNameRef.current);
        url.searchParams.set('participantName', participantName);
        url.searchParams.set('mode', mode);
        const resp = await fetch(url.toString());
        if (!resp.ok) throw new Error('Failed to get connection details');
        const data = await resp.json();
        await room.connect(data.serverUrl, data.participantToken, { autoSubscribe: true });
        await room.localParticipant.setMicrophoneEnabled(true);

        // Connection is only considered successful when an agent participant actually joins.
        if (room.remoteParticipants.size > 0) return;
        await new Promise<void>((resolve) => {
          const onParticipantConnected = () => {
            room.off(RoomEvent.ParticipantConnected, onParticipantConnected);
            resolve();
          };
          room.on(RoomEvent.ParticipantConnected, onParticipantConnected);
        });
      })();

      await Promise.race([connectFlow, timeoutPromise]);
      if (connectAttemptIdRef.current !== attemptId) return;
      setStatus('connected');
    } catch (err) {
      if (connectAttemptIdRef.current !== attemptId) return;
      try {
        await room.disconnect();
      } catch {
        // Ignore disconnect errors while handling failed connect attempts.
      }
      roomNameRef.current = `agent-${mode}-${Date.now()}`;
      setError(err instanceof Error ? err.message : 'Connection failed');
      setStatus('idle');
    } finally {
      if (connectTimeoutRef.current !== null) {
        clearTimeout(connectTimeoutRef.current);
        connectTimeoutRef.current = null;
      }
    }
  }, [room, mode, participantName]);

  const disconnect = useCallback(async () => {
    await room.disconnect();
    roomNameRef.current = `agent-${mode}-${Date.now()}`;
    setStatus('idle');
  }, [room, mode]);

  useEffect(() => {
    const onDisconnected = () => {
      roomNameRef.current = `agent-${mode}-${Date.now()}`;
      setStatus('idle');
    };
    room.on(RoomEvent.Disconnected, onDisconnected);
    return () => { room.off(RoomEvent.Disconnected, onDisconnected); };
  }, [room, mode]);

  useEffect(() => {
    if (!error) return;

    const onGlobalInteraction = () => {
      scheduleErrorReset();
    };

    window.addEventListener('pointerdown', onGlobalInteraction);
    window.addEventListener('keydown', onGlobalInteraction);
    window.addEventListener('touchstart', onGlobalInteraction, { passive: true });
    return () => {
      window.removeEventListener('pointerdown', onGlobalInteraction);
      window.removeEventListener('keydown', onGlobalInteraction);
      window.removeEventListener('touchstart', onGlobalInteraction);
    };
  }, [error, scheduleErrorReset]);

  useEffect(() => {
    return () => {
      if (connectTimeoutRef.current !== null) {
        clearTimeout(connectTimeoutRef.current);
        connectTimeoutRef.current = null;
      }
      if (errorResetTimeoutRef.current !== null) {
        clearTimeout(errorResetTimeoutRef.current);
        errorResetTimeoutRef.current = null;
      }
      room.disconnect();
    };
  }, [room]);

  useEffect(() => {
    const onJoinStart = () => {
      setIsHidden(true);
      void room.disconnect();
    };
    window.addEventListener(INTERVIEW_JOIN_START_EVENT, onJoinStart);
    return () => {
      window.removeEventListener(INTERVIEW_JOIN_START_EVENT, onJoinStart);
    };
  }, [room]);

  const startDrag = useCallback((e: React.PointerEvent) => {
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    const el = containerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    dragStateRef.current = {
      pointerX: e.clientX,
      pointerY: e.clientY,
      elemBottom: window.innerHeight - rect.bottom,
      elemRight: window.innerWidth - rect.right,
      started: false,
    };
  }, []);

  const onHeaderPointerDown = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    e.preventDefault();
    startDrag(e);
  }, [startDrag]);

  useEffect(() => {
    const onMove = (e: PointerEvent) => {
      const drag = dragStateRef.current;
      if (!drag) return;
      const dx = e.clientX - drag.pointerX;
      const dy = e.clientY - drag.pointerY;
      if (!drag.started && Math.hypot(dx, dy) < 6) return;
      drag.started = true;
      setIsDragging(true);
      setPos(clampToViewport(drag.elemBottom - dy, drag.elemRight - dx));
    };

    const onUp = () => {
      if (!dragStateRef.current) return;
      const wasDragging = dragStateRef.current.started;
      dragStateRef.current = null;
      setIsDragging(false);
      if (wasDragging) {
        suppressNextClickRef.current = true;
      }
    };

    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
    };
  }, [clampToViewport]);

  useEffect(() => {
    const onResize = () => {
      setPos(prev => (prev ? clampToViewport(prev.bottom, prev.right) : prev));
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [clampToViewport]);

  useEffect(() => {
    if (status !== 'connected') return;
    const frame = window.requestAnimationFrame(() => {
      setPos(prev => (prev ? clampToViewport(prev.bottom, prev.right) : prev));
    });
    return () => window.cancelAnimationFrame(frame);
  }, [status, clampToViewport]);

  const label = mode === 'admin' ? 'Admin Assistant' : 'Interview Assistant';
  const showInlineError = status === 'idle' && !!error;
  const inlineErrorText = showInlineError ? 'Connection failed. Tap to retry.' : '';

  if (isHidden) {
    return null;
  }

  if (status === 'idle' || status === 'connecting') {
    return (
      <div
        ref={containerRef}
        className={styles.container}
        data-agent-chat-widget="true"
        style={pos ? { bottom: `${pos.bottom}px`, right: `${pos.right}px` } : undefined}
        onPointerDown={startDrag}
      >
        <button
          className={`${styles.fabButton} ${showInlineError ? styles.fabButtonError : ''}`}
          onClick={() => {
            if (suppressNextClickRef.current) {
              suppressNextClickRef.current = false;
              return;
            }
            void connect();
          }}
          disabled={status === 'connecting' || !participantName}
          title={showInlineError ? error ?? `Start ${label}` : `Start ${label}`}
        >
          <PhoneIcon />
          <span className={styles.fabButtonTextWrap}>
            <span className={styles.fabButtonLabel}>
              {status === 'connecting' ? 'Connecting…' : startButtonLabel}
            </span>
            {showInlineError && (
              <span className={styles.fabButtonSubtext}>{inlineErrorText}</span>
            )}
          </span>
          {status === 'connecting' && <span className={styles.spinner} />}
        </button>
      </div>
    );
  }

  return (
    <RoomContext.Provider value={room}>
      <WidgetInner
        label={label}
        status={status}
        isDragging={isDragging}
        setStatus={setStatus}
        disconnect={disconnect}
        startDrag={startDrag}
        onHeaderPointerDown={onHeaderPointerDown}
        containerRef={containerRef}
        pos={pos}
        suppressNextClickRef={suppressNextClickRef}
      />
    </RoomContext.Provider>
  );
}

/* ── Inner widget (rendered inside RoomContext) ─────────────────────────── */

interface InnerProps {
  label: string;
  status: Status;
  isDragging: boolean;
  setStatus: (s: Status) => void;
  disconnect: () => void;
  startDrag: (e: React.PointerEvent) => void;
  onHeaderPointerDown: (e: React.PointerEvent<HTMLDivElement>) => void;
  containerRef: React.RefObject<HTMLDivElement>;
  pos: { bottom: number; right: number } | null;
  suppressNextClickRef: React.MutableRefObject<boolean>;
}

function WidgetInner({
  label,
  status,
  isDragging,
  setStatus,
  disconnect,
  startDrag,
  onHeaderPointerDown,
  containerRef,
  pos,
  suppressNextClickRef,
}: InnerProps) {
  const room = useRoomContext();
  const { chatMessages, send } = useChat();
  const { localParticipant, isMicrophoneEnabled } = useLocalParticipant();
  const remoteParticipants = useRemoteParticipants();
  const [agentMuted, setAgentMuted] = useState(false);
  const [isAgentSpeaking, setIsAgentSpeaking] = useState(false);
  const [isUserSpeaking, setIsUserSpeaking] = useState(false);
  const [inputText, setInputText] = useState('');
  const [transcripts, setTranscripts] = useState<Map<string, DisplayMessage>>(new Map());
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to latest message
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages, transcripts, isUserSpeaking]);

  // Listen for STT transcriptions from both local user and agent
  useEffect(() => {
    const onTranscription = (
      segments: TranscriptionSegment[],
      participant?: Participant,
    ) => {
      setTranscripts(prev => {
        const next = new Map(prev);
        for (const seg of segments) {
          next.set(seg.id, {
            kind: 'transcript',
            id: seg.id,
            text: seg.text,
            isLocal: participant?.isLocal ?? false,
            ts: prev.get(seg.id)?.ts ?? seg.firstReceivedTime ?? Date.now(),
            isFinal: seg.final,
          });
        }
        return next;
      });
    };
    room.on(RoomEvent.TranscriptionReceived, onTranscription);
    return () => { room.off(RoomEvent.TranscriptionReceived, onTranscription); };
  }, [room]);

  // Merge chat messages + transcripts into one sorted display list
  const displayMessages = useMemo<DisplayMessage[]>(() => {
    const chatItems: DisplayMessage[] = chatMessages.map(m => ({
      kind: 'chat',
      id: m.id,
      text: m.message,
      isLocal: m.from?.isLocal ?? false,
      ts: m.timestamp,
    }));
    const transcriptItems = Array.from(transcripts.values());
    return [...chatItems, ...transcriptItems].sort((a, b) => a.ts - b.ts);
  }, [chatMessages, transcripts]);

  // Locally mute/unmute agent audio tracks
  useEffect(() => {
    for (const participant of remoteParticipants) {
      for (const pub of participant.trackPublications.values()) {
        if (pub.kind === Track.Kind.Audio && pub.track?.mediaStreamTrack) {
          pub.track.mediaStreamTrack.enabled = !agentMuted;
        }
      }
    }
  }, [agentMuted, remoteParticipants]);

  useEffect(() => {
    const onActiveSpeakersChanged = (speakers: Participant[]) => {
      setIsUserSpeaking(speakers.some(speaker => speaker.isLocal));
      setIsAgentSpeaking(speakers.some(speaker => !speaker.isLocal));
    };

    room.on(RoomEvent.ActiveSpeakersChanged, onActiveSpeakersChanged);
    return () => {
      room.off(RoomEvent.ActiveSpeakersChanged, onActiveSpeakersChanged);
    };
  }, [room]);

  const toggleMic = useCallback(() => {
    localParticipant.setMicrophoneEnabled(!isMicrophoneEnabled);
  }, [localParticipant, isMicrophoneEnabled]);

  const sendMessage = useCallback(async () => {
    const text = inputText.trim();
    if (!text) return;
    await send(text);
    setInputText('');
  }, [send, inputText]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        sendMessage();
      }
    },
    [sendMessage],
  );

  // Collapsed: small FAB with live indicator
  if (status === 'collapsed') {
    return (
      <div
        ref={containerRef}
        className={styles.container}
        data-agent-chat-widget="true"
        style={pos ? { bottom: `${pos.bottom}px`, right: `${pos.right}px` } : undefined}
        onPointerDown={startDrag}
      >
        <RoomAudioRenderer />
        <button
          className={`${styles.fabButton} ${styles.fabButtonActive}`}
          onClick={() => {
            if (suppressNextClickRef.current) {
              suppressNextClickRef.current = false;
              return;
            }
            setStatus('connected');
          }}
          title={`${label} — tap to expand`}
        >
          <PhoneIcon />
          <span>Expand</span>
          <span className={styles.liveDot} />
        </button>
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className={styles.container}
      data-agent-chat-widget="true"
      style={pos ? { bottom: `${pos.bottom}px`, right: `${pos.right}px` } : undefined}
    >
      <div className={styles.widget}>
        <RoomAudioRenderer />

        {/* ── Header ─────────────────────────────────────────────────── */}
        <div
          className={`${styles.header} ${isDragging ? styles.headerDragging : ''}`}
          onPointerDown={onHeaderPointerDown}
        >
          <div className={styles.headerTitle}>
            <span className={styles.liveDot} />
            <span>{label}</span>
          </div>
          <div className={styles.headerActions}>
            <button
              className={styles.iconBtn}
              onClick={() => setStatus('collapsed')}
              title="Collapse (stays connected)"
              aria-label="Collapse"
            >
              <ChevronDownIcon />
            </button>
          </div>
        </div>

        {/* ── Messages ───────────────────────────────────────────────── */}
        <div className={styles.messages}>
          {displayMessages.length === 0 ? (
            <div className={styles.emptyState}>
              <MicIconLg />
              <p>Please hold on a moment while we connect you to the agent...</p>
            </div>
          ) : (
            displayMessages.map(msg => {
              const isPending = msg.kind === 'transcript' && !msg.isFinal;
              return (
                <div
                  key={msg.id}
                  className={`${styles.msgRow} ${msg.isLocal ? styles.msgRowUser : styles.msgRowAgent}`}
                >
                  {!msg.isLocal && (
                    <span className={styles.agentBadge}>Agent</span>
                  )}
                  <div className={`${styles.msgBubble} ${isPending ? styles.msgBubblePending : ''}`}>
                    {msg.text}
                  </div>
                </div>
              );
            })
          )}
          {SHOW_LISTENING_ANIMATION && isUserSpeaking && isMicrophoneEnabled && (
            <ListeningIndicator />
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* ── Footer ─────────────────────────────────────────────────── */}
        <div className={styles.footer}>
          <div className={styles.controlRow}>
            <div className={`${styles.controlRingWrap} ${isAgentSpeaking && !agentMuted ? styles.controlRingActiveAgent : ''}`}>
              <button
                className={`${styles.controlBtn} ${agentMuted ? styles.controlBtnOff : ''}`}
                onClick={() => setAgentMuted(v => !v)}
                title={agentMuted ? 'Unmute agent audio' : 'Mute agent audio'}
                aria-label={agentMuted ? 'Unmute agent' : 'Mute agent'}
              >
                {agentMuted ? <VolumeOffIcon /> : <VolumeOnIcon />}
              </button>
            </div>
            <div className={`${styles.controlRingWrap} ${isUserSpeaking && isMicrophoneEnabled ? styles.controlRingActiveUser : ''}`}>
              <button
                className={`${styles.controlBtn} ${!isMicrophoneEnabled ? styles.controlBtnOff : ''}`}
                onClick={toggleMic}
                title={isMicrophoneEnabled ? 'Mute microphone' : 'Unmute microphone'}
                aria-label={isMicrophoneEnabled ? 'Mute mic' : 'Unmute mic'}
              >
                {isMicrophoneEnabled ? <MicIcon /> : <MicOffIcon />}
              </button>
            </div>
            <button
              className={`${styles.controlBtn} ${styles.controlBtnDanger}`}
              onClick={disconnect}
              title="End call"
              aria-label="Disconnect"
            >
              <PhoneOffIcon />
            </button>
          </div>

          <div className={styles.inputRow}>
            <input
              className={styles.input}
              value={inputText}
              onChange={e => setInputText(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Type a message…"
              aria-label="Message input"
            />
            <button
              className={styles.sendBtn}
              onClick={sendMessage}
              disabled={!inputText.trim()}
              title="Send message"
              aria-label="Send"
            >
              <SendIcon />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── SVG icon components ────────────────────────────────────────────────── */

function PhoneIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12 19.79 19.79 0 0 1 1.61 3.32 2 2 0 0 1 3.6 1h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 8.6a16 16 0 0 0 5.49 5.49l.96-.96a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z" />
    </svg>
  );
}

function PhoneOffIcon() {
  return (
    <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M10.68 13.31a16 16 0 0 0 3.41 2.6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7 2 2 0 0 1 1.72 2v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.42 19.42 0 0 1-3.33-2.67m-2.67-3.34a19.79 19.79 0 0 1-3.07-8.63A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91" />
      <line x1="23" y1="1" x2="1" y2="23" />
    </svg>
  );
}

function ChevronDownIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <polyline points="6 9 12 15 18 9" />
    </svg>
  );
}

function MicIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
      <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
      <line x1="12" y1="19" x2="12" y2="23" />
      <line x1="8" y1="23" x2="16" y2="23" />
    </svg>
  );
}

function MicIconLg() {
  return (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
      <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
      <line x1="12" y1="19" x2="12" y2="23" />
      <line x1="8" y1="23" x2="16" y2="23" />
    </svg>
  );
}

function MicOffIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <line x1="1" y1="1" x2="23" y2="23" />
      <path d="M9 9v3a3 3 0 0 0 5.12 2.12M15 9.34V4a3 3 0 0 0-5.94-.6" />
      <path d="M17 16.95A7 7 0 0 1 5 12v-2m14 0v2a7 7 0 0 1-.11 1.23" />
      <line x1="12" y1="19" x2="12" y2="23" />
      <line x1="8" y1="23" x2="16" y2="23" />
    </svg>
  );
}

function VolumeOnIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
      <path d="M19.07 4.93a10 10 0 0 1 0 14.14" />
      <path d="M15.54 8.46a5 5 0 0 1 0 7.07" />
    </svg>
  );
}

function VolumeOffIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
      <line x1="23" y1="9" x2="17" y2="15" />
      <line x1="17" y1="9" x2="23" y2="15" />
    </svg>
  );
}

function SendIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <line x1="22" y1="2" x2="11" y2="13" />
      <polygon points="22 2 15 22 11 13 2 9 22 2" />
    </svg>
  );
}

function ListeningIndicator() {
  return (
    <div className={styles.listeningRow} aria-live="polite">
      <div className={styles.listeningBars} aria-hidden>
        {[0, 1, 2, 3, 4].map(i => (
          <span
            key={i}
            className={styles.listeningBar}
            style={{ animationDelay: `${i * 0.1}s` }}
          />
        ))}
      </div>
      <span className={styles.listeningLabel}>Listening...</span>
    </div>
  );
}
